import { useState, useEffect, useCallback } from "react";
import { useAuthStore } from "../../store/authStore";
import { userAPI } from "./userAPI";
import { sanitizeInputLight, sanitizeInput } from "../../lib/sanitizer";
import MySchoolsList from "../schools/MySchoolsList";
import Avatar from "../../components/Avatar";

export default function UserProfile() {
  const { user } = useAuthStore();
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    avatar: user?.avatar || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchUserStats = useCallback(async () => {
    try {
      const result = await userAPI.getUser(user.id);
      if (result.success) {
        setUserStats(result.data.stats);
        setUserProfile(result.data.user);
      }
    } catch (err) {
      console.error("Error fetching user stats:", err);
    }
  }, [user.id]);

  useEffect(() => {
    const loadUserStats = async () => {
      if (user?.id) {
        await fetchUserStats();
      }
    };

    loadUserStats();
  }, [user?.id, fetchUserStats]);

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      name: user?.name || "",
      avatar: user?.avatar || "",
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user?.name || "",
      avatar: user?.avatar || "",
    });
    setError("");
    setSuccess("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sử dụng sanitize nhẹ cho trường name khi đang gõ
    let sanitizedValue = value;
    if (name === "name") {
      sanitizedValue = sanitizeInputLight(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Tên không được để trống");
      return;
    }

    if (formData.name.trim().length < 2) {
      setError("Tên phải có ít nhất 2 ký tự");
      return;
    }

    if (formData.name.trim().length > 50) {
      setError("Tên không được vượt quá 50 ký tự");
      return;
    }

    // Kiểm tra avatar URL nếu có
    if (formData.avatar && formData.avatar.trim()) {
      try {
        new URL(formData.avatar);
      } catch {
        setError("URL avatar không hợp lệ");
        return;
      }
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Sanitize data trước khi gửi
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        avatar: formData.avatar.trim(),
      };

      const result = await userAPI.updateProfile(sanitizedData);

      if (result.success) {
        setSuccess("Cập nhật thông tin thành công");
        setIsEditing(false);
        // Update user in auth store
        const { getProfile } = useAuthStore.getState();
        await getProfile();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi không mong muốn");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!user) {
    return (
      <div className="profile__login-required">
        <p>Vui lòng đăng nhập để xem thông tin cá nhân</p>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="profile__header">
        <h1 className="profile__title">Thông tin cá nhân</h1>
        <p className="profile__subtitle">
          Quản lý thông tin và theo dõi hoạt động của bạn
        </p>
      </div>

      {/* Profile Card */}
      <div className="profile__card">
        <div className="profile__card-header">
          <div className="profile__user-info">
            <Avatar user={user} size="lg" />

            <div className="profile__user-details">
              <h2 className="profile__name">{user.name}</h2>
              <p className="profile__email">{user.email}</p>
              {userProfile?.createdAt && (
                <p className="profile__join-date">
                  Tham gia từ {formatDate(userProfile.createdAt)}
                </p>
              )}
              {user.role === "admin" && (
                <span className="profile__admin-badge">Quản trị viên</span>
              )}
            </div>
          </div>

          {!isEditing && (
            <button onClick={handleEdit} className="btn btn--primary">
              Chỉnh sửa
            </button>
          )}
        </div>

        {/* Edit Form */}
        {isEditing && (
          <form onSubmit={handleSubmit} className="profile__edit-form">
            <h3 className="profile__edit-title">Chỉnh sửa thông tin</h3>

            <div className="profile__form-grid">
              <div className="profile__form-field">
                <label className="profile__form-label">Tên hiển thị</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="profile__form-input"
                  maxLength="50"
                  required
                />
              </div>

              <div className="profile__form-field">
                <label className="profile__form-label">Avatar (URL)</label>
                <input
                  type="url"
                  name="avatar"
                  value={formData.avatar}
                  onChange={handleChange}
                  className="profile__form-input"
                  placeholder="https://i.postimg.cc/example.jpg"
                />

                {/* Hướng dẫn sử dụng avatar */}
                <div className="profile__avatar-guide">
                  <p className="profile__avatar-guide-title">
                    📝 Hướng dẫn sử dụng avatar:
                  </p>
                  <ul className="profile__avatar-guide-list">
                    <li>
                      ✅ <strong>Định dạng:</strong> JPG, PNG, GIF, WebP
                    </li>
                    <li>
                      ✅ <strong>Kích thước:</strong> Tối đa 2MB (ảnh thường)
                      hoặc 3MB (GIF)
                    </li>
                    <li>
                      ✅ <strong>Nguồn tin cậy:</strong> Imgur, Postimg,
                      Unsplash, GitHub...
                    </li>
                    <li>
                      ✅ <strong>Phải là link HTTPS</strong> trỏ trực tiếp đến
                      file ảnh
                    </li>
                    <li>
                      ❌ <strong>Không được:</strong> Embed code, link từ
                      website lạ
                    </li>
                  </ul>
                  <div className="profile__avatar-guide-examples">
                    <p className="profile__avatar-guide-subtitle">
                      💡 Cách upload:
                    </p>
                    <ol>
                      <li>
                        Upload ảnh lên{" "}
                        <a
                          href="https://imgur.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Imgur
                        </a>{" "}
                        hoặc{" "}
                        <a
                          href="https://postimg.cc"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          PostImg
                        </a>
                      </li>
                      <li>Click chuột phải vào ảnh → "Copy image address"</li>
                      <li>Paste link vào ô trên</li>
                    </ol>
                  </div>
                  <p className="profile__avatar-guide-recommend">
                    🔗 Khuyến nghị: Sử dụng{" "}
                    <a
                      href="https://postimg.cc"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      PostImg
                    </a>{" "}
                    hoặc{" "}
                    <a
                      href="https://imgur.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Imgur
                    </a>{" "}
                    để upload ảnh
                  </p>
                </div>

                {/* Preview avatar */}
                {formData.avatar && (
                  <div className="avatar-preview">
                    <span className="avatar-preview__label">Preview:</span>
                    <div className="avatar-preview__container">
                      <div className="avatar-preview__avatar">
                        <img
                          src={formData.avatar}
                          alt="Avatar preview"
                          className="avatar-preview__img"
                          onLoad={(e) => {
                            e.target.style.display = "block";
                            e.target.nextSibling.style.display = "none";
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div className="avatar-preview__fallback">
                          <span className="avatar-preview__fallback-icon">
                            ❌
                          </span>
                          <small>Không thể tải</small>
                        </div>
                      </div>
                      <div className="avatar-preview__info">
                        <div className="avatar-preview__info-type">
                          {formData.avatar.toLowerCase().includes(".gif")
                            ? "🎬 GIF Animation"
                            : "🖼️ Ảnh tĩnh"}
                        </div>
                        <span className="avatar-preview__info-status avatar-preview__info-status--success">
                          Đã tải thành công
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="profile__error">
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className="profile__success">
                <p>{success}</p>
              </div>
            )}

            <div className="profile__form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn--secondary"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn--primary"
              >
                {loading ? "Đang lưu..." : "Lưu thay đổi"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Statistics */}
      {userStats && (
        <div className="profile__stats">
          <div className="profile__stat-card profile__stat-card--reviews">
            <div className="profile__stat-icon">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </div>
            <div className="profile__stat-info">
              <p className="profile__stat-label">Đánh giá</p>
              <p className="profile__stat-value">{userStats.reviewCount}</p>
            </div>
          </div>

          <div className="profile__stat-card profile__stat-card--comments">
            <div className="profile__stat-icon">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div className="profile__stat-info">
              <p className="profile__stat-label">Bình luận</p>
              <p className="profile__stat-value">{userStats.commentCount}</p>
            </div>
          </div>

          <div className="profile__stat-card profile__stat-card--schools">
            <div className="profile__stat-icon">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="profile__stat-info">
              <p className="profile__stat-label">Trường đã thêm</p>
              <p className="profile__stat-value">{userStats.schoolCount}</p>
            </div>
          </div>

          <div className="profile__stat-card profile__stat-card--rating">
            <div className="profile__stat-icon">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                />
              </svg>
            </div>
            <div className="profile__stat-info">
              <p className="profile__stat-label">Đánh giá trung bình</p>
              <p className="profile__stat-value">
                {userStats.avgRating?.toFixed(1) || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="profile__actions">
        <h3 className="profile__actions-title">Hành động nhanh</h3>

        <div className="profile__actions-grid">
          <a href="/schools" className="profile__action-card">
            <div className="profile__action-icon profile__action-icon--search">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <div className="profile__action-content">
              <p className="profile__action-title">Tìm trường</p>
              <p className="profile__action-description">
                Khám phá các trường học
              </p>
            </div>
          </a>

          <a href="/schools/create" className="profile__action-card">
            <div className="profile__action-icon profile__action-icon--add">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <div className="profile__action-content">
              <p className="profile__action-title">Thêm trường</p>
              <p className="profile__action-description">
                Đóng góp thông tin trường mới
              </p>
            </div>
          </a>

          <a href="/reviews" className="profile__action-card">
            <div className="profile__action-icon profile__action-icon--reviews">
              <svg
                className="icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </div>
            <div className="profile__action-content">
              <p className="profile__action-title">Xem đánh giá</p>
              <p className="profile__action-description">
                Đọc các đánh giá mới nhất
              </p>
            </div>
          </a>
        </div>
      </div>

      {/* My Schools List */}
      <MySchoolsList />
    </div>
  );
}
